// pages/LocationSearch/index.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import styled from "styled-components";
import { Common } from "../../styles/Common";
import SearchIcon from "@src/assets/Signup/Search";
import { getLocation, type LocationItem } from "@src/api/expert/expertApi";
import { useDebounce } from "@src/hooks/useDebounce";
import { useSignupExpertStore } from "@src/store/signupExpertStore";

type ResultItem = LocationItem & { displayAddress: string };



const LocationSearch = () => {

  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const debounced = useDebounce(query, 300);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ResultItem[]>([]);
  const { form, setPartialState } = useSignupExpertStore();


  const handleClick = (item: ResultItem) => {
    setPartialState({
      form: {
        ...form,
        officeInfo: {
          ...form.officeInfo,
          address: item.displayAddress,
        },
      },
    });
    navigate(-1);
  };

  useEffect(() => {
    let aborted = false;

    if (!debounced.trim()) {
      setResults([]);
      setError(null);
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getLocation({ searchText: debounced });
        const normalized: ResultItem[] = (res.list ?? []).map((it: any) => {
          const v =
            [it?.address, it?._source?.roadAddr, it?._source?.jibunAddr, it?.name]
              .find((s) => typeof s === "string") ?? "";
          return { ...it, displayAddress: String(v) };
        }).filter((it) => !!it.displayAddress);
        if (!aborted) setResults(normalized);
      } catch {
        if (!aborted) setError("소재지 검색 중 오류가 발생했습니다.");
      } finally {
        if (!aborted) setLoading(false);
      }
    })();

    return () => {
      aborted = true;
    };
  }, [debounced]);

  const showGuide = useMemo(
    () => !debounced.trim() && !results.length && !loading && !error,
    [debounced, results, loading, error]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return (
    <Location_Layout>
      <Header title="소재지 검색" />
      <Location_Container>
        <SearchBar>
          <IconWrapper>
            <SearchIcon />
          </IconWrapper>
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="소재지검색"
          />
        </SearchBar>

        {error && <ErrorText>{error}</ErrorText>}

        {showGuide && (
          <Text_Layout>
            <div className="title">이렇게 검색해보세요.</div>
            <div className="subtitle">도로명 + 건물번호</div>
            <div className="example">예) 언주로 30길 13</div>
            <div className="subtitle">동/읍/면/리 + 번지</div>
            <div className="example">예) 도곡동 467-6</div>
            <div className="subtitle">건물명, 아파트명</div>
            <div className="example">예) 대림아크로빌</div>
          </Text_Layout>
        )}

        {!loading && !error && results.length > 0 && (
          <ResultList>
            {results.map((item, idx) => (
              <ResultItemLi
                key={String((item as any)?._id ?? idx)}
                onClick={() => handleClick(item)}
                style={{ cursor: "pointer" }}
              >
                <AddressLine>{item.displayAddress}</AddressLine>
              </ResultItemLi>
            ))}
          </ResultList>
        )}
      </Location_Container>
    </Location_Layout>
  );
};

export default LocationSearch;

const Location_Layout = styled.div``;

const Location_Container = styled.div`
  padding: 24px;
`;

const SearchBar = styled.div`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #2275ee;
  background-color: #fbf8f8;
  border-radius: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 16px;
  line-height: 1.4;
  color: ${Common.colors?.normalTextColor ?? "#111"};
  ::placeholder {
    color: #9da6b3;
  }
`;

const ResultList = styled.ul`
  margin-top: 12px;
  border: 1px solid #a3a3a3;
  overflow: hidden;
  max-height: 100vh;
  overflow-y: auto;
`;

const ResultItemLi = styled.li`
  padding: 14px 16px;
  background: #fff;
  cursor: pointer;
  border-bottom: 1px solid #f0f2f5;
  &:last-child {
    border-bottom: none;
  }
`;

const AddressLine = styled.div`
  font-size: 14px;
  line-height: 140%;
  font-weight: 400;
  color: #16181d;
`;

const ErrorText = styled.div`
  margin-top: 12px;
  font-size: 14px;
  color: #d14343;
`;

const Text_Layout = styled.div`
  .title {
    margin-top: 16px;
    margin-bottom: 8px;
    font-size: 16px;
    color: #2275ee;
    font-weight: 400;
    line-height: 140%;
  }
  .subtitle {
    margin-left: 10px;
    font-size: 12px;
    color: #3c3c3c;
    font-weight: 400;
    line-height: 140%;
  }
  .example {
    margin-left: 10px;
    font-size: 12px;
    color: #a3a3a3;
    font-weight: 400;
    line-height: 140%;
    margin-bottom: 4px;
  }
`;
